/* External */
import { call, put, takeEvery } from 'redux-saga/effects';

/* Ott-common */
import { SectionId } from '@dtvgo/backend-service/normalize';

/* ActionTypes */
import { FETCH_CAROUSEL } from './actionTypes';

/* Actions */
import { entityActions } from './actions';
import { addToEntities } from '../utils/actions';

/* Sagas */
import { requestSaga } from '../requests/sagas';
import { authenticatedSaga } from '../utils/sagas';

/* Services */
import backendService from '../../services/backendService';

/* Utils */
import {
  deleteTitleFromContentContinueWatching,
  removeDurationAndProgressFromContentCarousel,
} from '../utils/preprocessors';
import { MY_LIST_PARAMS, SOURCE_CAROUSEL_ID, TA_VOD_PARAMS } from '../../utils/constants';
import {
  isHomeBannerRowType,
  isRowAdsCommunicationRowType,
  isToastBannerRowType,
  normalizeRowTypeString,
} from '../../utils/landerCommunicationHelpers';
import {
  isLanderBannerActive,
  isPagesV5Active,
  isRowAdsBannerActive,
  isToastBannerActive,
  useCarouselV5,
} from '../../utils/env';

function isCommunicationCarouselFetchEnabled(type) {
  if (!type || typeof type !== 'string') return false;
  if (isHomeBannerRowType(type)) {
    return Boolean(isLanderBannerActive);
  }
  if (isToastBannerRowType(type)) {
    return isRowAdsCommunicationRowType(type)
      ? Boolean(isRowAdsBannerActive)
      : Boolean(isToastBannerActive);
  }
  if (normalizeRowTypeString(type) === normalizeRowTypeString(SOURCE_CAROUSEL_ID.ROW_ADS)) {
    return Boolean(isRowAdsBannerActive);
  }
  return false;
}

function usesCarouselCommunicationEndpoint(type) {
  if (!type || typeof type !== 'string') return false;
  return (
    isHomeBannerRowType(type)
    || isToastBannerRowType(type)
    || normalizeRowTypeString(type) === normalizeRowTypeString(SOURCE_CAROUSEL_ID.ROW_ADS)
  );
}

function fetchCarouselService({
  sourceCarouselId,
  source,
  type,
  filters,
  max,
  isCheddar,
  recommendationType,
}) {
  if (sourceCarouselId === SectionId.MOST_WATCHED) {
    return backendService.experience.v3.getMostWatchedChannels();
  }

  if (usesCarouselCommunicationEndpoint(type)) {
    if (!isCommunicationCarouselFetchEnabled(type)) {
      return undefined;
    }
    return backendService.layout.v4.getCarouselCommunication(sourceCarouselId);
  }

  if (isPagesV5Active) {
    const serviceVersion = useCarouselV5 ? 'v5' : 'v4';
    return isCheddar
      ? backendService.layout[serviceVersion].getCarouselGeneral(
        { sourceCarouselId, source, type },
        recommendationType,
        filters,
        { max },
      )
      : backendService.layout[serviceVersion].getCarouselInsight(
        { sourceCarouselId, source, type },
        recommendationType,
        filters,
        { max },
      );
  }

  return backendService.layout.v3.getCarousel(
    { sourceCarouselId, source, type },
    filters,
    { max },
  );
}

function* fetchCarouselSaga(action) {
  try {
    const response = yield call(requestSaga, fetchCarouselService, action);
    if (response) {
      const { normalizedData: { entities, result } } = response;
      const { carousels } = entities;
      const carouselsTypes = [MY_LIST_PARAMS.TYPE, TA_VOD_PARAMS.TYPE];
      let newEntities = entities;

      if (result === SectionId.MOST_WATCHED) {
        const { sourceCarouselId } = action.payload;
        yield put(entityActions.add({
          [sourceCarouselId]: {
            sourceCarouselId,
            contents: carousels[sourceCarouselId].contents ?? [],
          },
        }));
        return;
      }

      if (carouselsTypes.includes(action.payload.type)) {
        newEntities = removeDurationAndProgressFromContentCarousel(entities);
      }

      yield put(addToEntities(deleteTitleFromContentContinueWatching(newEntities)));
    } else {
      const { sourceCarouselId } = action.payload;
      yield put(entityActions.add({
        [sourceCarouselId]: {
          sourceCarouselId,
          contents: [],
        },
      }));
    }
  } catch (error) {
    console.error('carousels>fetchCarouselSaga', error);
    const { sourceCarouselId } = action.payload;
    yield put(entityActions.add({
      [sourceCarouselId]: {
        sourceCarouselId,
        contents: [],
      },
    }));
  }
}

function* fetchCarousel() {
  yield takeEvery(FETCH_CAROUSEL, authenticatedSaga(fetchCarouselSaga));
}

export default [
  fetchCarousel,
];
